import React, { useEffect, useState } from "react";
import {
    Box, Typography, Grid, Card, CardContent, CardHeader, CircularProgress,
    Button, Avatar, Chip, Link as MuiLink, CardActions, IconButton
} from "@mui/material";
import { Helmet } from "react-helmet";
import { Crumbs } from '../Crumbs.jsx';
import ReactMarkdown from "react-markdown";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArticleIcon from '@mui/icons-material/Article';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventIcon from '@mui/icons-material/Event';
import InfoIcon from '@mui/icons-material/Info';

const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const LinksPage = () => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLinks = async () => {
            try {
                const res = await fetch(`${BACKEND_HOST}/links?sort=title&populate=user_profile.pic`);
                const data = await res.json();
                setLinks(data.data || []);
            } catch (e) {
                console.error('Error fetching links:', e);
                setLinks([]);
            }
            setLoading(false);
        };
        fetchLinks();
    }, []);

    // Helper function to get link type styling and behavior
    const getLinkTypeConfig = (link) => {
        const linkType = link.linkType || link.type || 'general';
        
        switch (linkType.toLowerCase()) {
            case 'video':
            case 'youtube':
                return {
                    icon: <PlayArrowIcon />,
                    color: 'error',
                    buttonText: 'Watch Video',
                    bgColor: 'error.dark'
                };
            case 'article':
            case 'blog':
                return {
                    icon: <ArticleIcon />,
                    color: 'info',
                    buttonText: 'Read Article',
                    bgColor: 'info.dark'
                };
            case 'shop':
            case 'store':
            case 'shopping':
                return {
                    icon: <ShoppingCartIcon />,
                    color: 'success',
                    buttonText: 'Visit Store',
                    bgColor: 'success.dark'
                };
            case 'event':
            case 'calendar':
                return {
                    icon: <EventIcon />,
                    color: 'warning',
                    buttonText: 'View Event',
                    bgColor: 'warning.dark'
                };
            case 'resource':
            case 'tool':
                return {
                    icon: <InfoIcon />,
                    color: 'secondary',
                    buttonText: 'Use Resource',
                    bgColor: 'secondary.dark'
                };
            default:
                return {
                    icon: <OpenInNewIcon />,
                    color: 'primary',
                    buttonText: 'Visit Link',
                    bgColor: 'primary.dark'
                };
        }
    };

    const title = "Useful Links for Two-Step Dancing";
    const desc = "A collection of helpful resources, websites, and links provided by the two-step dancing community.";

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Helmet>
                    <meta charSet="utf-8"/>
                    <meta property="og:description" content={desc}/>
                    <meta property="twitter:title" content={title}/>
                    <meta property="twitter:description" content={desc}/>
                    <title>{title}</title>
                    <meta property="description" content={desc} />
                    <link rel="canonical" href="https://austin2step.com/links"/>
                </Helmet>
                <Crumbs crumbs={[
                    {title: "Community"},
                    {title: "Links"}
                ]} />
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" color="primary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {desc}
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12}>
                {loading ? (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                ) : links.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" align="center">
                                No links found. Check back soon for helpful resources!
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {links.map((link, idx) => {
                            const userProfile = link.user_profile;
                            const profilePic = userProfile?.pic?.formats?.thumbnail?.url || 
                                             userProfile?.pic?.url;
                            const typeConfig = getLinkTypeConfig(link);
                            
                            return (
                                <Grid item xs={12} md={6} lg={4} key={link.id || idx}>
                                    <Card sx={{ 
                                        height: '100%', 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        position: 'relative',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            transition: 'transform 0.2s ease-in-out'
                                        }
                                    }}>
                                        {/* Link Type Indicator */}
                                        <Box 
                                            sx={{ 
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: typeConfig.bgColor,
                                                borderRadius: '50%',
                                                p: 0.5,
                                                zIndex: 1
                                            }}
                                        >
                                            {typeConfig.icon}
                                        </Box>
                                        
                                        <CardHeader
                                            avatar={
                                                profilePic ? (
                                                    <Avatar 
                                                        src={profilePic}
                                                        alt={userProfile?.title || 'User'}
                                                    />
                                                ) : userProfile?.title ? (
                                                    <Avatar>
                                                        {userProfile.title.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                ) : (
                                                    <Avatar />
                                                )
                                            }
                                            title={link.title}
                                            subheader={userProfile?.title ? `Shared by ${userProfile.title}` : 'Community Link'}
                                        />
                                        <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                                            {link.description && (
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    <ReactMarkdown>{link.description}</ReactMarkdown>
                                                </Typography>
                                            )}
                                            
                                            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                {/* Link Type Chip */}
                                                {(link.linkType || link.type) && (
                                                    <Chip 
                                                        label={link.linkType || link.type} 
                                                        size="small" 
                                                        color={typeConfig.color}
                                                        variant="filled"
                                                        icon={typeConfig.icon}
                                                    />
                                                )}
                                                
                                                {/* Category Chip */}
                                                {link.category && (
                                                    <Chip 
                                                        label={link.category} 
                                                        size="small" 
                                                        color="default"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                            
                                            {/* Tags */}
                                            {link.tags && (
                                                <Box mb={2}>
                                                    {link.tags.split(',').map((tag, index) => (
                                                        <Chip 
                                                            key={index}
                                                            label={tag.trim()} 
                                                            size="small" 
                                                            sx={{ mr: 0.5, mb: 0.5 }}
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </CardContent>
                                        
                                        {/* Action Button */}
                                        <CardActions sx={{ p: 2, pt: 0 }}>
                                            {link.url && (
                                                <Button
                                                    variant="contained"
                                                    color={typeConfig.color}
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    fullWidth
                                                    startIcon={typeConfig.icon}
                                                >
                                                    {typeConfig.buttonText}
                                                </Button>
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default LinksPage;